import { getCanonicalUrl } from "@/utils/seoUtils";
import { ELF_EMERGENCY_GUIDES } from "@/data/elfEmergencyGuides";
import { buildGuideSchema } from "@/data/elfEmergencySeo";

export const DEFAULT_OG_IMAGE = "https://elfletterbot.com/elf-letter-template.png";

export type RouteMeta = {
  title: string;
  description: string;
  canonicalUrl?: string;
  ogImage?: string;
  ogType?: string;
  noIndex?: boolean;
  schema?: Record<string, unknown>;
};

export const STATIC_ROUTE_META: Record<string, RouteMeta> = {
  "/": {
    title: "Elf Letter Bot | Create Magical Elf Letters in Seconds",
    description:
      "Find free Christmas ideas and create personalized elf letters with 3 free PDF deliveries per email address. Discover more at The Magic Letter Box.",
    canonicalUrl: getCanonicalUrl("/"),
    ogImage: DEFAULT_OG_IMAGE,
    ogType: "website",
    schema: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "WebSite",
          "@id": "https://elfletterbot.com/#website",
          name: "Elf Letter Bot",
          alternateName: "ElfLetterBot",
          url: "https://elfletterbot.com",
          inLanguage: "en-US",
          publisher: { "@id": "https://elfletterbot.com/#organization" },
        },
        {
          "@type": "Organization",
          "@id": "https://elfletterbot.com/#organization",
          name: "Elf Letter Bot",
          url: "https://elfletterbot.com",
          logo: {
            "@type": "ImageObject",
            url: "https://elfletterbot.com/logo.png",
          },
          sameAs: ["https://themagicletterbox.com"],
          description:
            "Create magical, personalized letters from your family's Christmas elf. Free elf letter generator with printable PDFs.",
        },
        {
          "@type": "WebApplication",
          name: "Free Elf Letter Generator",
          url: getCanonicalUrl("/elf-letter-generator"),
          description:
            "Create personalized elf letters with 3 free printable PDF deliveries per email address.",
          applicationCategory: "EntertainmentApplication",
          operatingSystem: "Web",
          offers: {
            "@type": "Offer",
            price: "0",
            priceCurrency: "USD",
            availability: "https://schema.org/OnlineOnly",
          },
          publisher: {
            "@id": "https://elfletterbot.com/#organization",
          },
        },
      ],
    },
  },
  "/elf-letter-generator": {
    title: "Free Elf Letter Generator | Elf Letter Bot",
    description:
      "Create personalized elf letters with 3 free PDF deliveries per email address. Explore The Magic Letter Box for unlimited letters with Family membership.",
    canonicalUrl: getCanonicalUrl("/elf-letter-generator"),
    ogImage: DEFAULT_OG_IMAGE,
    ogType: "website",
    schema: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "WebApplication",
          name: "Free Elf Letter Generator",
          url: getCanonicalUrl("/elf-letter-generator"),
          description: "Create personalized elf letters with 3 free printable PDF deliveries per email address.",
          applicationCategory: "EntertainmentApplication",
          operatingSystem: "Web",
          offers: { "@type": "Offer", price: "0", priceCurrency: "USD", availability: "https://schema.org/OnlineOnly" },
          publisher: { "@type": "Organization", name: "Elf Letter Bot", url: getCanonicalUrl("/") },
        },
        {
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: getCanonicalUrl("/") },
            { "@type": "ListItem", position: 2, name: "Elf Letter Generator", item: getCanonicalUrl("/elf-letter-generator") },
          ],
        },
      ],
    },
  },
  "/elf-name-generator": {
    title: "Christmas Elf Name Generator | Elf Letter Bot",
    description:
      "Generate playful Christmas elf names instantly — try festive first names, full elf names, and save favorites. Free tool for holiday traditions and elf letters.",
    canonicalUrl: getCanonicalUrl("/elf-name-generator"),
    ogImage: DEFAULT_OG_IMAGE,
    ogType: "website",
    schema: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "WebApplication",
          name: "Christmas Elf Name Generator",
          url: getCanonicalUrl("/elf-name-generator"),
          description: "Generate playful Christmas elf names instantly for family traditions, arrival letters, and holiday fun.",
          applicationCategory: "EntertainmentApplication",
          operatingSystem: "Web",
          offers: { "@type": "Offer", price: "0", priceCurrency: "USD", availability: "https://schema.org/OnlineOnly" },
          publisher: { "@type": "Organization", name: "Elf Letter Bot", url: getCanonicalUrl("/") },
        },
        {
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: getCanonicalUrl("/") },
            { "@type": "ListItem", position: 2, name: "Elf Name Generator", item: getCanonicalUrl("/elf-name-generator") },
          ],
        },
      ],
    },
  },
  "/blog": {
    title: "Christmas Elf Ideas, Tips & Inspiration | Elf Letter Bot Blog",
    description:
      "Discover magical elf on the shelf ideas, Christmas craft projects, holiday recipes, and family tradition tips. Free inspiration for your elf adventures.",
    canonicalUrl: getCanonicalUrl("/blog"),
    ogImage: DEFAULT_OG_IMAGE,
    ogType: "website",
    schema: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "CollectionPage",
          name: "Elf Letter Bot Blog",
          description:
            "Discover magical elf on the shelf ideas, Christmas craft projects, holiday recipes, and family tradition tips. Free inspiration for your elf adventures.",
          url: getCanonicalUrl("/blog"),
          isPartOf: {
            "@type": "WebSite",
            name: "Elf Letter Bot",
            url: getCanonicalUrl("/"),
          },
        },
        {
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: getCanonicalUrl("/") },
            { "@type": "ListItem", position: 2, name: "Blog", item: getCanonicalUrl("/blog") },
          ],
        },
      ],
    },
  },
  "/about": {
    title: "About Us | Elf Letter Bot",
    description:
      "Meet Donnie, founder of Elf Letter Bot and The Magic Letter Box. Learn how free Christmas resources connect to the paid Family membership.",
    canonicalUrl: getCanonicalUrl("/about"),
    ogImage: DEFAULT_OG_IMAGE,
    ogType: "website",
    schema: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "AboutPage",
          name: "About Elf Letter Bot",
          description: "Meet Donnie, founder of Elf Letter Bot and The Magic Letter Box, and learn about our free resources and paid Family membership.",
          url: getCanonicalUrl("/about"),
          isPartOf: { "@type": "WebSite", name: "Elf Letter Bot", url: getCanonicalUrl("/") },
        },
        {
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: getCanonicalUrl("/") },
            { "@type": "ListItem", position: 2, name: "About", item: getCanonicalUrl("/about") },
          ],
        },
      ],
    },
  },
  "/elf-help": {
    title: "Christmas Elf Help: Forgot to Move, Late Arrival & More | Elf Letter Bot",
    description: "Quick, kind answers for Christmas elf surprises. Find a short example note, then create a personal letter at The Magic Letter Box.",
    canonicalUrl: getCanonicalUrl("/elf-help"),
    ogImage: DEFAULT_OG_IMAGE,
  },
  ...Object.fromEntries(ELF_EMERGENCY_GUIDES.filter((guide) => !guide.existingArticlePath).map((guide) => {
    const canonicalUrl = getCanonicalUrl(`/elf-help/${guide.slug}`);
    return [`/elf-help/${guide.slug}`, {
      title: `${guide.title} | Elf Letter Bot`,
      description: guide.description,
      canonicalUrl,
      ogImage: DEFAULT_OG_IMAGE,
      ogType: "article",
      schema: buildGuideSchema(guide, canonicalUrl),
    }];
  })),
  "/terms": {
    title: "Terms & Conditions | Elf Letter Bot",
    description:
      "Read Elf Letter Bot's terms and conditions — usage rules, content guidelines, and policies for our free elf letter generator and blog tools.",
    canonicalUrl: getCanonicalUrl("/terms"),
    ogImage: DEFAULT_OG_IMAGE,
    ogType: "website",
    schema: {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: getCanonicalUrl("/") },
        { "@type": "ListItem", position: 2, name: "Terms & Conditions", item: getCanonicalUrl("/terms") },
      ],
    },
  },
  "/privacy": {
    title: "Privacy Policy | Elf Letter Bot",
    description:
      "Elf Letter Bot's privacy policy — learn how we collect, use, and protect your data when using our free elf letter generator and Christmas blog tools.",
    canonicalUrl: getCanonicalUrl("/privacy"),
    ogImage: DEFAULT_OG_IMAGE,
    ogType: "website",
    schema: {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: getCanonicalUrl("/") },
        { "@type": "ListItem", position: 2, name: "Privacy Policy", item: getCanonicalUrl("/privacy") },
      ],
    },
  },
};
